import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Download, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const LABELS = { "play-1080x1920": "Google Play · 1080x1920",
                 "apple-6.9-1290x2796": "Apple 6.9\" · 1290x2796",
                 "apple-6.5-1242x2688": "Apple 6.5\" · 1242x2688" };

/** Drives the real app through a headless browser and saves store-exact phone screenshots. */
export const ScreenshotStudio = ({ app, onNotice }) => {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/mobile-apps/${app.id}/screenshots`);
      setJob(data);
      return data;
    } catch (e) {
      setError(errMsg(e));
      return null;
    }
  }, [app.id]);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (job?.status === "running") timer.current = setTimeout(load, 3000);
    return () => clearTimeout(timer.current);
  }, [job, load]);

  const start = async () => {
    setBusy(true); setError("");
    try {
      setJob((await api.post(`/admin/mobile-apps/${app.id}/screenshots`)).data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/admin/mobile-apps/${app.id}/screenshots.zip`,
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${app.app_id}-screenshots.zip`;
      link.click();
      URL.revokeObjectURL(url);
      onNotice?.("Screenshots downloaded.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const running = job?.status === "running";
  const shots = job?.shots || [];
  const groups = Object.keys(LABELS).filter((size) => shots.some((s) => s.size === size));

  return (
    <div data-testid="screenshot-studio" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-display text-lg text-slate-100">
            <Camera className="h-4 w-4 text-gold" /> Screenshot studio
          </p>
          <p className="mt-1 text-sm text-slate-400">
            Every screen of this app, at the exact sizes Play and Apple ask for. Takes about a
            minute — the browser signs in as the demo reviewer account.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="screenshots-generate" disabled={busy || running} onClick={start}
            className="bg-gold text-ink-900 hover:bg-gold-light">
            {busy || running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                             : <Camera className="mr-2 h-4 w-4" />}
            {running ? "Taking…" : shots.length ? "Take again" : "Generate screenshots"}
          </Button>
          {Boolean(shots.length) && !running && (
            <Button data-testid="screenshots-download" variant="outline" disabled={busy}
              onClick={download}>
              <Download className="mr-2 h-4 w-4" /> Download zip
            </Button>
          )}
        </div>
      </div>

      {error && <p data-testid="screenshots-error" className="mt-3 text-sm text-red-400">{error}</p>}
      {job?.error && !running && (
        <p data-testid="screenshots-job-error" className="mt-3 text-sm text-amber-400">{job.error}</p>
      )}
      {running && (
        <p data-testid="screenshots-progress" className="mt-3 font-mono text-[11px] uppercase tracking-widest text-gold">
          {job.done} of {job.total || "…"} captured
        </p>
      )}

      {groups.map((size) => (
        <div key={size} data-testid={`screenshots-group-${size}`} className="mt-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{LABELS[size]}</p>
          <div className="mt-2 flex gap-3 overflow-x-auto pb-2">
            {shots.filter((s) => s.size === size).map((shot) => (
              <a key={shot.name} href={shot.url} target="_blank" rel="noopener noreferrer"
                data-testid={`screenshot-${shot.page}-${size}`} className="shrink-0">
                <img src={shot.url} alt={shot.page}
                  className="h-56 w-auto rounded-lg border border-ink-600 transition-transform hover:scale-[1.02]" />
                <p className="mt-1 text-center font-mono text-[10px] text-slate-500">{shot.page}</p>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
